import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useRef, useSyncExternalStore } from 'react';

interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SpotlightOverlayProps {
  targetSelector: string;
  padding?: number;
  unblurSelectors?: string[];
  isVisible: boolean;
  transition?: 'crossfade' | 'none';
  overlay?: 'spotlight' | 'passthrough';
  stepKey?: string;
}

function clippedRect(el: Element): DOMRect | null {
  const r = el.getBoundingClientRect();
  const top = Math.max(0, r.top);
  const left = Math.max(0, r.left);
  const bottom = Math.min(window.innerHeight, r.bottom);
  const right = Math.min(window.innerWidth, r.right);
  if (bottom <= top || right <= left) return null;
  return new DOMRect(left, top, right - left, bottom - top);
}

function measureElement(
  selector: string,
  padding: number
): SpotlightRect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = clippedRect(el);
  if (!rect) return null;
  return {
    x: Math.max(0, rect.left - padding),
    y: Math.max(0, rect.top - padding),
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

function buildCutoutPath(rect: SpotlightRect): string {
  const { x, y, width, height } = rect;
  const r = 8;
  return `${x + r}px ${y}px, ${x + width - r}px ${y}px, ${x + width}px ${y + r}px, ${x + width}px ${y + height - r}px, ${x + width - r}px ${y + height}px, ${x + r}px ${y + height}px, ${x}px ${y + height - r}px, ${x}px ${y + r}px, ${x + r}px ${y}px`;
}

function buildClipPath(rects: SpotlightRect[]): string {
  if (rects.length === 0) return 'none';
  const cutouts = rects.map(r => `${buildCutoutPath(r)}, 0px 0px`).join(', ');
  return `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${cutouts})`;
}

function rectsEqual(a: SpotlightRect | null, b: SpotlightRect | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
  );
}

function rectArraysEqual(
  a: (SpotlightRect | null)[],
  b: (SpotlightRect | null)[]
): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) => rectsEqual(r, b[i]));
}

const EMPTY_RECTS: (SpotlightRect | null)[] = [];

function useTargetRects(
  selectors: string[],
  padding: number,
  isVisible: boolean
): (SpotlightRect | null)[] {
  const storeRef = useRef({ key: '', rects: EMPTY_RECTS });
  const selectorsKey =
    isVisible && selectors.length > 0 ? selectors.join('|') : '';

  const selectorsRef = useRef(selectors);
  const paddingRef = useRef(padding);
  // eslint-disable-next-line react-hooks/refs -- syncing props into refs for useSyncExternalStore subscribe closure
  selectorsRef.current = selectors;
  // eslint-disable-next-line react-hooks/refs
  paddingRef.current = padding;

  const subscribeKeyRef = useRef<string | null>(null);
  const subscribeRef = useRef<((notify: () => void) => () => void) | undefined>(
    undefined
  );

  /* eslint-disable react-hooks/refs -- intentional getDerivedStateFromProps pattern for useSyncExternalStore */
  if (subscribeKeyRef.current !== selectorsKey) {
    subscribeKeyRef.current = selectorsKey;
    subscribeRef.current = (notify: () => void) => {
      storeRef.current = { key: selectorsKey, rects: EMPTY_RECTS };

      if (!selectorsKey) {
        notify();
        return () => {};
      }

      let resizeObservers: ResizeObserver[] = [];
      let mutationObserver: MutationObserver | undefined;
      const observedSelectors = new Set<string>();

      let pendingRaf = 0;

      const measure = () => {
        const sels = selectorsRef.current;
        const pad = paddingRef.current;
        const next = sels.map((s, i) => measureElement(s, i === 0 ? pad : 4));

        if (!rectArraysEqual(storeRef.current.rects, next)) {
          storeRef.current = { key: selectorsKey, rects: next };
          notify();
        }

        for (const s of sels) {
          if (observedSelectors.has(s)) continue;
          const el = document.querySelector(s);
          if (el) {
            observedSelectors.add(s);
            const ro = new ResizeObserver(measure);
            ro.observe(el);
            resizeObservers.push(ro);
          }
        }

        if (observedSelectors.size === sels.length) {
          mutationObserver?.disconnect();
          mutationObserver = undefined;
        }
      };

      measure();

      // Brief rAF polling after step change to track elements that are
      // mid-CSS-transition (drawer slide-in). Runs for ~500ms then stops.
      const settleStart = performance.now();
      const SETTLE_DURATION = 500;
      const tickSettle = () => {
        if (performance.now() - settleStart > SETTLE_DURATION) {
          pendingRaf = 0;
          return;
        }
        measure();
        pendingRaf = requestAnimationFrame(tickSettle);
      };
      pendingRaf = requestAnimationFrame(tickSettle);

      if (observedSelectors.size < selectorsRef.current.length) {
        mutationObserver = new MutationObserver(() => {
          const sels = selectorsRef.current;
          for (const s of sels) {
            if (!observedSelectors.has(s) && document.querySelector(s)) {
              measure();
              break;
            }
          }
        });
        mutationObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      window.addEventListener('scroll', measure, true);
      window.addEventListener('resize', measure);

      return () => {
        cancelAnimationFrame(pendingRaf);
        resizeObservers.forEach(ro => ro.disconnect());
        resizeObservers = [];
        mutationObserver?.disconnect();
        window.removeEventListener('scroll', measure, true);
        window.removeEventListener('resize', measure);
      };
    };
  }
  /* eslint-enable react-hooks/refs */

  // eslint-disable-next-line react-hooks/refs -- useSyncExternalStore requires ref reads during render
  return useSyncExternalStore(subscribeRef.current!, () =>
    storeRef.current.key === selectorsKey ? storeRef.current.rects : EMPTY_RECTS
  );
}

const FADE_MS = 0.15;

export function SpotlightOverlay({
  targetSelector,
  padding = 8,
  unblurSelectors,
  isVisible,
  transition = 'crossfade',
  overlay = 'spotlight',
  stepKey,
}: SpotlightOverlayProps) {
  const prefersReducedMotion = useReducedMotion();
  const allSelectors = [targetSelector, ...(unblurSelectors ?? [])];
  const measurements = useTargetRects(allSelectors, padding, isVisible);

  const mainRect = measurements[0] ?? null;
  const cutoutRects = measurements.filter(
    (r): r is SpotlightRect => r !== null
  );
  const displayRect = mainRect;
  const noMotion = !!prefersReducedMotion || transition === 'none';
  const fadeDuration = noMotion ? 0 : FADE_MS;
  const isCrossfade = transition === 'crossfade' && !prefersReducedMotion;
  const highlightKey = isCrossfade && stepKey ? `hl-${stepKey}` : 'highlight';

  const highlightTransition = noMotion
    ? { duration: 0 }
    : {
        opacity: { duration: FADE_MS },
        left: { duration: 0 },
        top: { duration: 0 },
        width: { duration: 0 },
        height: { duration: 0 },
      };

  const ready = isVisible;

  return (
    <AnimatePresence mode="sync">
      {ready && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: fadeDuration }}
          className={`fixed inset-0 z-[300]${overlay === 'passthrough' ? 'pointer-events-none' : ''}`}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            clipPath: buildClipPath(cutoutRects),
          }}
          aria-hidden="true"
        />
      )}
      {ready && displayRect && (
        <motion.div
          key={highlightKey}
          initial={isCrossfade ? { opacity: 0 } : false}
          animate={{
            opacity: 1,
            left: displayRect.x,
            top: displayRect.y,
            width: displayRect.width,
            height: displayRect.height,
          }}
          exit={{ opacity: 0 }}
          transition={highlightTransition}
          className="pointer-events-none fixed z-[300]"
          style={{
            borderRadius: 8,
            border: '2px solid rgba(239, 68, 68, 0.7)',
            boxShadow:
              '0 0 0 2px rgba(239, 68, 68, 0.5), 0 0 12px 4px rgba(239, 68, 68, 0.3)',
          }}
          aria-hidden="true"
        />
      )}
    </AnimatePresence>
  );
}
